/**
 * HIPAA Session Timeout Warning Component
 * 
 * Displays a warning modal when user session is about to expire
 * Allows user to extend session or logout
 */

import { useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Typography } from "@/components/ui/typography";
import { useSessionTimeout } from "@/hooks/useSessionTimeout";
import { AlertTriangle } from "lucide-react";
import { RootState } from "@/store/store";
import { useSelector } from 'react-redux';

export const SessionTimeoutWarning = () => {
  const { showWarning, timeRemaining, resetTimeout } = useSessionTimeout();
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);

  useEffect(() => {
    // Play alert sound when warning appears (optional)
    if (showWarning) {
      console.log("Session timeout warning displayed");
    }
  }, [showWarning]);

  // Close dialog and reset timer when user logs out
  useEffect(() => {
    if (!isAuthenticated && showWarning) {
      resetTimeout();
    }
  }, [isAuthenticated, showWarning, resetTimeout]);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Don't show dialog when user is not authenticated
  if (!isAuthenticated) {
    return null;
  }

  return (
    <Dialog open={showWarning} onOpenChange={() => {}}>
      <DialogContent 
        className="sm:max-w-md"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-warning-100 dark:bg-warning-900/20 rounded-full">
              <AlertTriangle className="h-6 w-6 text-warning-600 dark:text-warning-500" />
            </div>
            <DialogTitle>Session Timeout Warning</DialogTitle>
          </div>
        </DialogHeader>
        
        <DialogDescription className="py-4">
          <div className="flex flex-col gap-4">
            <Typography variant="regular-16" className="text-grey-700 dark:text-grey-300 text-sm">
              Your session will expire soon due to inactivity. To continue your session,
              simply interact with the app (move your mouse, scroll, or type).
            </Typography>
            
            <div className="p-4 bg-grey-100 dark:bg-grey-800 rounded-md">
              <Typography variant="semibold-16" className="text-center">
                Time Remaining: {formatTime(timeRemaining)}
              </Typography>
            </div>
            
            <Typography variant="regular-16" className="text-grey-600 dark:text-grey-400 text-xs">
              If you do nothing, you will be automatically logged out to protect sensitive data.
            </Typography>
          </div>
        </DialogDescription>
      </DialogContent>
    </Dialog>
  );
};

